import { useCallback, useMemo, useState } from 'react';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  MoreHorizontal,
  PauseCircle,
  Plus,
  Search,
  UserCog,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  VirtualTable,
  type VirtualColumn,
} from '@/components/ui/virtual-table';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { ArchiveCompanyDialog } from './ArchiveCompanyDialog';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { CompanyInvitationsPanel } from './CompanyInvitationsPanel';
import { CompanyUsersPanel } from './CompanyUsersPanel';
import { CreateCompanyDialog } from './CreateCompanyDialog';
import {
  useImpersonateCompany,
  usePlatformCompaniesInfinite,
  useRestoreCompany,
  useSetCompanyAccessState,
} from './hooks/usePlatformAdminQueries';
import type { TenantStatus, TenantSummary } from './__mocks__/platform-admin';
import {
  formatNumber,
  formatPaidUntil,
  statusBadgeVariant,
  statusLabel,
  tariffBadgeVariant,
  tariffLabel,
} from './format';
import { cn } from '@/lib/utils';

type StatusFilter = TenantStatus | 'all';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'Все статусы' },
  { value: 'active', label: 'Active' },
  { value: 'trial', label: 'Trial' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
  { value: 'suspended', label: 'Suspended' },
];

// Фильтра по тарифу здесь нет намеренно: живой реестр (`/platform/companies`)
// тариф не отдаёт, и селект «Профи» ничего бы не отфильтровал. Контрол,
// который делает вид, что работает, хуже отсутствующего — человек уверен, что
// список сужен, а он полный. Вернуть вместе с тарифом в ответе.

/** Текст отказа сервера, если он есть; иначе — честный дефолт. */
function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.detail || fallback : fallback;
}

/**
 * Действия над компанией. До AK-572 три из четырёх пунктов писали в консоль
 * браузера, а «Открыть» вело на несуществующий маршрут — меню выглядело
 * рабочим и не делало ничего.
 *
 * Приостановка и архив — РАЗНЫЕ выключатели, и в одном меню их видно рядом
 * намеренно. Приостановка живёт на биллинговой оси (`access_state`) и
 * снимается пересчётом подписки; архив — на `tenants.status`, из-за которого
 * компания исчезает из доступных пользователю. Подменить одно другим нельзя:
 * пришедший платёж «расархивировал» бы компанию сам.
 */
function RowActions({
  tenant,
  onExpand,
  onArchive,
  onSuspendRequest,
  onNotice,
}: {
  tenant: TenantSummary;
  onExpand: () => void;
  onArchive: () => void;
  onSuspendRequest: (next: boolean) => void;
  onNotice: (text: string) => void;
}) {
  const enterCompany = useImpersonateCompany();
  const restore = useRestoreCompany();
  const archived = tenant.status === 'archived';
  const suspended = tenant.status === 'suspended';

  const enter = async () => {
    try {
      await enterCompany.mutateAsync({ tenantId: tenant.id });
      // Cookie уже смотрит в чужой тенант, а в памяти — сторы админки.
      // Жёсткий переход гарантирует чистый старт, как и при входе под
      // конкретным пользователем.
      window.location.assign('/');
    } catch (err) {
      toast.error(errorText(err, 'Не удалось войти в компанию'));
    }
  };

  const restoreFromArchive = async () => {
    // try/catch обязателен: голый `void mutateAsync(...)` при любом отказе
    // (409 «не в архиве», 403, сеть) закрывал меню, ничего не менял и ронял
    // unhandled rejection в консоль — тот самый молчаливый no-op, ради
    // истребления которого этот PR и затевался.
    try {
      await restore.mutateAsync({ tenantId: tenant.id });
      onNotice(`Компания «${tenant.name}» возвращена из архива.`);
    } catch (err) {
      toast.error(errorText(err, 'Не удалось вернуть компанию из архива'));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          aria-label={`Действия для ${tenant.name}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuItem onSelect={onExpand}>
          <Eye className="h-4 w-4" />
          Пользователи и приглашения
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void enter()}>
          <UserCog className="h-4 w-4" />
          Войти в компанию
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        {/* Два соседних пункта — разные выключатели, и человеку это ниоткуда
            не видно. Подпись под каждым: приостановка снимается сама при
            оплате, архив возвращают руками. */}
        {/* Архивной компании приостановку не предлагаем: `status` —
            производная колонка, где `archived` перекрывает `suspended`, и
            пункт предлагал бы приостановить то, что и так вне системы. */}
        {!archived && (
          <DropdownMenuItem
            className="flex-col items-start gap-0"
            onSelect={() => onSuspendRequest(!suspended)}
          >
            <span className="flex items-center gap-2">
              <PauseCircle className="h-4 w-4" />
              {suspended ? 'Снять приостановку' : 'Приостановить доступ'}
            </span>
            <span className="pl-6 text-xs text-muted-foreground">
              Оплата и доступ; снимается сама при оплате
            </span>
          </DropdownMenuItem>
        )}

        {archived ? (
          <DropdownMenuItem
            className="flex-col items-start gap-0"
            onSelect={() => void restoreFromArchive()}
          >
            <span className="flex items-center gap-2">
              <ArchiveRestore className="h-4 w-4" />
              Вернуть из архива
            </span>
            <span className="pl-6 text-xs text-muted-foreground">
              Компания снова появится у своих сотрудников
            </span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            className="flex-col items-start gap-0"
            onSelect={onArchive}
          >
            <span className="flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Архивировать
            </span>
            <span className="pl-6 text-xs text-muted-foreground">
              Компания уходит из системы; вернуть — только руками
            </span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Раскрытая строка компании: пользователи и приглашения (AK-572).
 *
 * Вкладками, а не двумя списками подряд — раскрытие живёт внутри строки
 * таблицы, и вертикаль там дорогая: два развёрнутых блока вытолкнули бы
 * следующие компании за экран.
 */
function CompanyExpandedRow({
  tenantId,
  tenantName,
}: {
  tenantId: string;
  tenantName: string;
}) {
  const [tab, setTab] = useState<'users' | 'invites'>('users');

  return (
    <div className="border-b border-border/70 bg-muted/40 px-3 py-2">
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'users' | 'invites')}>
        <TabsList>
          <TabsTrigger value="users">Пользователи</TabsTrigger>
          <TabsTrigger value="invites">Приглашения</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <CompanyUsersPanel tenantId={tenantId} tenantName={tenantName} />
        </TabsContent>
        <TabsContent value="invites">
          <CompanyInvitationsPanel tenantId={tenantId} tenantName={tenantName} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function PlatformAdminCompaniesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<TenantSummary | null>(null);
  // Подтверждение приостановки/снятия. Цена ошибки та же, что у архивации —
  // утром никто в компании не работает; отличается только цена отката.
  const [suspendTarget, setSuspendTarget] = useState<{
    tenant: TenantSummary;
    next: boolean;
  } | null>(null);
  const [suspendError, setSuspendError] = useState<string | null>(null);
  // Успех показываем строкой на экране, а не тостом: `lib/toast.success`
  // сегодня только пишет в консоль (реального тостера в проекте нет), и
  // «обратная связь» была бы невидимой.
  const [notice, setNotice] = useState<string | null>(null);
  const accessState = useSetCompanyAccessState();

  const applySuspend = async () => {
    if (!suspendTarget) return;
    const { tenant, next } = suspendTarget;
    setSuspendError(null);
    try {
      await accessState.mutateAsync({
        tenantId: tenant.id,
        accessState: next ? 'suspended' : 'active',
        reason: next ? 'Приостановлено администратором платформы' : undefined,
      });
      setSuspendTarget(null);
      setNotice(
        next
          ? `Доступ компании «${tenant.name}» приостановлен.`
          : `Приостановка с компании «${tenant.name}» снята.`,
      );
    } catch (err) {
      setSuspendError(errorText(err, 'Не удалось изменить доступ компании'));
    }
  };
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  // Раскрытая строка — одна: раскрытие показывает список пользователей
  // компании, держать несколько открытыми смысла нет (AK-470).
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpanded = useCallback((t: TenantSummary) => {
    setExpandedId((prev) => (prev === t.id ? null : t.id));
  }, []);

  const filter = useMemo(() => ({ search, status }), [search, status]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePlatformCompaniesInfinite(filter);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  const resetKey = useMemo(
    () => JSON.stringify([search, status]),
    [search, status],
  );

  const columns: VirtualColumn<TenantSummary>[] = useMemo(
    () => [
      {
        // Раскрытие строки в список пользователей (AK-470, wireframe экран 3).
        key: 'expander',
        header: '',
        width: '32px',
        className: 'px-0 text-center',
        cell: (t) =>
          expandedId === t.id ? (
            <ChevronDown className="mx-auto h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="mx-auto h-4 w-4 text-muted-foreground" />
          ),
      },
      {
        key: 'name',
        header: 'Название',
        width: 'minmax(180px, 1.4fr)',
        cell: (t) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(t);
            }}
            className="truncate text-left font-medium text-foreground hover:text-primary"
          >
            {t.name}
          </button>
        ),
      },
      {
        key: 'slug',
        header: 'Slug',
        width: 'minmax(140px, 1fr)',
        cell: (t) => (
          <span className="truncate font-mono text-xs text-muted-foreground">
            {t.slug}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Статус',
        width: '120px',
        cell: (t) => (
          <Badge variant={statusBadgeVariant(t.status)}>
            {statusLabel(t.status)}
          </Badge>
        ),
      },
      {
        key: 'tariff',
        header: 'Тариф',
        width: '130px',
        // Прочерк, а не Badge с прочерком внутри: пустая ячейка должна
        // выглядеть одинаково во всех трёх колонках, которых реестр не
        // отдаёт (тариф, площадь, «платно до»). Значок в форме бейджа
        // читается как значение.
        cell: (t) =>
          t.tariff == null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <Badge variant={tariffBadgeVariant(t.tariff)}>
              {tariffLabel(t.tariff)}
            </Badge>
          ),
      },
      {
        key: 'users',
        header: 'Юзеры',
        width: '90px',
        align: 'right',
        cell: (t) => formatNumber(t.users_count),
      },
      {
        key: 'area',
        header: 'Площадь, га',
        width: '120px',
        align: 'right',
        cell: (t) => (t.area_ha == null ? '—' : formatNumber(t.area_ha)),
      },
      {
        key: 'paid',
        header: 'Платно до',
        width: '130px',
        cell: (t) => (
          <span className="text-muted-foreground">
            {formatPaidUntil(t.paid_until)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: '56px',
        cell: (t) => (
          <RowActions
            tenant={t}
            onExpand={() => toggleExpanded(t)}
            onArchive={() => setArchiveTarget(t)}
            onSuspendRequest={(next) => {
              setSuspendError(null);
              setSuspendTarget({ tenant: t, next });
            }}
            onNotice={setNotice}
          />
        ),
      },
    ],
    [expandedId, toggleExpanded, setArchiveTarget],
  );

  const resetFilters = () => {
    setSearch('');
    setStatus('all');
  };

  return (
    <PlatformAdminLayout>
      <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Компании</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data
              ? `${formatNumber(total)} компаний найдено`
              : 'Загружаем список компаний…'}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Создать компанию
        </Button>
      </div>

      {notice && (
        <Alert className="mt-4">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{notice}</span>
            <Button variant="outline" size="sm" onClick={() => setNotice(null)}>
              Понятно
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <Card className="mt-5 flex min-h-0 flex-1 flex-col">
        {/* Toolbar */}
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по названию или slug…"
              className="pl-8"
            />
          </div>

          <Select value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button variant="outline" size="sm">
            <Download className="h-4 w-4" />
            Экспорт
          </Button>
        </div>

        {/* Error banner (top-level fetch failure) */}
        {isError && items.length === 0 && (
          <div className="flex items-start gap-2 border-b border-border bg-destructive/5 px-4 py-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="flex-1">
              <div className="font-medium">Не удалось загрузить компании</div>
              <div className="text-xs opacity-80">{error?.message ?? 'Неизвестная ошибка'}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Повторить
            </Button>
          </div>
        )}

        <div className="min-h-0 flex-1 p-3">
          <VirtualTable
            columns={columns}
            rows={items}
            rowKey={(t) => t.id}
            total={total}
            hasMore={!!hasNextPage}
            loading={isLoading}
            loadingMore={isFetchingNextPage}
            error={isError ? (error?.message ?? 'Ошибка загрузки') : null}
            onLoadMore={() => fetchNextPage()}
            onRetry={() => refetch()}
            onRowClick={toggleExpanded}
            expandedRowKey={expandedId}
            renderExpanded={(t) => (
              <CompanyExpandedRow tenantId={t.id} tenantName={t.name} />
            )}
            unitLabel="компаний"
            emptyText="Компании по фильтру не найдены"
            restoreKey="platform-companies"
            resetKey={resetKey}
            height="100%"
          />
        </div>

        {/* Empty-state helper actions */}
        {!isLoading && !isError && items.length === 0 && (
          <div className="flex justify-center gap-2 px-6 pb-6">
            <Button variant="outline" size="sm" onClick={resetFilters}>
              Сбросить фильтры
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Создать компанию
            </Button>
          </div>
        )}
      </Card>

      <p className={cn('mt-3 shrink-0 text-[11px] text-muted-foreground')}>
        Тариф, площадь и «платно до» платформенный реестр компаний пока не
        отдаёт — колонки показывают прочерк, фильтра по тарифу поэтому нет.
        Клик по строке раскрывает пользователей компании и приглашения.
      </p>
      </div>

      <CreateCompanyDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ConfirmActionDialog
        open={suspendTarget !== null}
        title={
          suspendTarget?.next
            ? `Приостановить доступ компании «${suspendTarget?.tenant.name}»?`
            : `Снять приостановку с «${suspendTarget?.tenant.name}»?`
        }
        description={
          suspendTarget?.next
            ? 'Сотрудники компании сразу увидят экран о приостановке и не смогут работать. Это про оплату: доступ вернётся сам, когда подписка станет активной, — данные и структура компании остаются на месте.'
            : 'Компания снова сможет работать. Если подписка не оплачена, приостановка может вернуться при следующем пересчёте.'
        }
        confirmLabel={
          suspendTarget?.next ? 'Приостановить' : 'Снять приостановку'
        }
        destructive={suspendTarget?.next === true}
        pending={accessState.isPending}
        error={suspendError}
        onConfirm={() => void applySuspend()}
        onOpenChange={(open) => {
          if (!open) {
            setSuspendTarget(null);
            setSuspendError(null);
          }
        }}
      />

      <ArchiveCompanyDialog
        tenantId={archiveTarget?.id ?? null}
        tenantName={archiveTarget?.name ?? ''}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      />
    </PlatformAdminLayout>
  );
}

export default PlatformAdminCompaniesPage;
